/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { useEffect, useMemo } from 'react';
import { useBasket } from '~/features/basket/hooks';
import { useCommerceAPI } from '../contexts';
import { isTokenValid } from '../utils';
import useCustomer from './useCustomer';
import { setCustomer } from './useCustomerStore';

const customerIdStorageKey = 'customer-id';
const instoreTokenStorageKey = 'instore-token';
const instoreBasketIdStorageKey = 'basket-id';

const hasInstoreSessionInStorage = (): boolean => {
    if (typeof window === 'undefined') return false;
    return Boolean(
        window.sessionStorage.getItem(instoreTokenStorageKey) ||
            window.sessionStorage.getItem(instoreBasketIdStorageKey),
    );
};

/**
 * Joins basket and customer hooks into a single hook for initializing their states
 * when the app loads on the client-side. Should only be use at top-level of app.
 * @returns {Object} - customer and basket objects
 */
const useShopper = (opts: { currency?: string } = {}) => {
    const { currency } = opts;
    const api = useCommerceAPI();
    const customer = useCustomer();
    const basket = useBasket({ currency });

    // Handle basket init/updates in response to customer/basket changes.
    useEffect(() => {
        const hasBasket = basket?.loaded;

        // We have a customer but no basket, so we fetch a new or existing basket
        if (customer.isInitialized && !hasBasket) {
            basket.getOrCreateBasket();
            return;
        }

        // We have a customer and a basket, but the basket does not belong to this customer
        // so we get their existing basket or create a new one for them
        if (
            hasBasket &&
            customer.isInitialized &&
            customer.customerId !== basket.customerInfo?.customerId
        ) {
            basket.getOrCreateBasket();
            return;
        }

        // We have a registered customer (customer with email), and we have their basket,
        // but the email applied to the basket is missing or doesn't match the customer
        // email. In this case, we update the basket with their email.
        if (
            hasBasket &&
            customer.isRegistered &&
            customer.customerId === basket.customerInfo?.customerId &&
            customer.email !== basket.customerInfo?.email &&
            !customer.isStorePersonnel
        ) {
            basket.updateCustomerInfo({ email: customer.email! });
            return;
        }

        /* If none of the above, login the customer */
        if (!customer.isInitialized) {
            // Rehydrate a registered session from stored SLAS tokens if present.
            // Passwordless token exchange can yield a valid access token but no refresh token, so we must
            // restore `customerId` from storage to avoid falling back to guest PKCE login.
            // Only do this when we're not a guest: guest customer IDs are stored too but getCustomer() returns 404 for them.
            if (typeof window !== 'undefined' && !hasInstoreSessionInStorage()) {
                const accessToken = window.localStorage.getItem('token');
                const storedCustomerId = window.localStorage.getItem(customerIdStorageKey);
                const guestRefreshToken = window.localStorage.getItem('cc-nx-g');
                if (
                    accessToken &&
                    isTokenValid(accessToken) &&
                    storedCustomerId &&
                    !guestRefreshToken
                ) {
                    // Confirm the stored customerId against the server before committing to
                    // 'registered' authType - never let Checkout (or anything else) observe an
                    // unverified guess, since stale localStorage on a shared terminal could
                    // otherwise briefly impersonate the wrong customer.
                    if (api.shopperCustomers) {
                        void api.shopperCustomers
                            .getCustomer({ parameters: { customerId: storedCustomerId } })
                            .then((c) => (c ? setCustomer(c) : customer.login()))
                            .catch(() => customer.login());
                    } else {
                        customer.login();
                    }
                    return;
                }
            }
            customer.login();
        }
    }, [customer.authType, basket.loaded]);

    const handlers = useMemo(
        () => ({
            getShipmentMethods: async (shipmentId?: string) => {
                if (!shipmentId) {
                    console.warn('No shipment id provided');
                    return;
                }
                return basket.getShippingMethods(shipmentId);
            },
        }),
        [customer, basket],
    );

    return { customer, basket, handlers };
};

export default useShopper;
